import { useEffect, useRef } from 'react';
import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify';
import { marked } from 'marked';
import { useTextContent } from './useTextContent';
import { InlineStatus } from './InlineStatus';

interface Props {
  contentUrl: string;
}

// Lock marked to the safest, most predictable rendering mode:
// - `gfm: true`      — tables, strikethrough, task lists
// - `breaks: false`  — single newlines are NOT <br/>, matches GitHub
// - NO custom renderer — default escaping
//
// The output is then passed through DOMPurify, which strips any inline
// event handlers, script tags, javascript: URLs, data: URLs on anchors,
// and unknown elements.
marked.setOptions({ gfm: true, breaks: false });

// Strip a leading YAML frontmatter block (`---\n...\n---\n`).
//
// Without this, marked interprets the top `---` as an `<hr>`, treats
// the body as a paragraph, and then *upgrades* that paragraph to a
// setext H2 because the trailing `---` sits directly under a text
// block. The net effect is a giant H2 containing the raw YAML, which
// is the single worst rendering of a frontmatter'd file we can produce.
//
// Stripping is purely presentational — the raw file is still available
// via the Download button, which returns the byte-identical source.
const FRONTMATTER_RE = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/;
function stripFrontmatter(src: string): string {
  return src.replace(FRONTMATTER_RE, '');
}

// Restrict the allowed anchor scheme list so only http(s) + mailto
// links survive sanitization. DOMPurify's `RETURN_DOM_FRAGMENT` makes
// sanitize return a real DocumentFragment we can attach via `ref` —
// this avoids the React innerHTML escape hatch entirely.
const PURIFY_OPTS: DOMPurifyConfig = {
  USE_PROFILES: { html: true },
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|#)/i,
  FORBID_TAGS: ['style', 'form', 'iframe', 'object', 'embed'],
  FORBID_ATTR: ['style', 'onerror', 'onclick', 'onload'],
  RETURN_DOM_FRAGMENT: true,
};

export function MarkdownView({ contentUrl }: Props) {
  const content = useTextContent(contentUrl);
  const articleRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = articleRef.current;
    if (!el) return;
    if (content.status !== 'ready') {
      el.replaceChildren();
      return;
    }
    // `marked.parse` is synchronous when no async extensions are
    // configured, though TS types it as `string | Promise<string>`.
    const body = stripFrontmatter(content.text);
    const rawHtml = marked.parse(body, { async: false }) as string;
    const fragment = DOMPurify.sanitize(rawHtml, PURIFY_OPTS) as unknown as DocumentFragment;
    el.replaceChildren(fragment);
  }, [content]);

  if (content.status === 'loading') return <InlineStatus label="Loading markdown…" />;
  if (content.status === 'error') return <InlineStatus label={`Error: ${content.message}`} error />;
  if (content.status === 'too-large')
    return <InlineStatus label="File is too large to render — use Download." error />;

  return <article ref={articleRef} className="md-body mx-auto max-w-4xl" />;
}
